/**
 * SINGLE SOURCE OF TRUTH for Firebase initialization.
 * All other modules must import from this file.
 * Pattern restored from Hackathon2025 branch for authority consistency.
 */
import { initializeApp, getApps, getApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth } from 'firebase/auth';
import { getFirestore, Firestore } from 'firebase/firestore';
import { getStorage, FirebaseStorage } from 'firebase/storage';
import { getFunctions, Functions } from 'firebase/functions';

// Unified env reader for Vite (client) and Node (SSR)
const readEnv = (name: string, legacy?: string): string => {
  const viteVal = (import.meta as any)?.env?.[name] as string | undefined;
  const viteLegacy = legacy ? ((import.meta as any)?.env?.[legacy] as string | undefined) : undefined;
  const nodeVal = (typeof process !== 'undefined' ? (process as any).env?.[name] : undefined) as string | undefined;
  return viteVal || viteLegacy || nodeVal || '';
};

const firebaseConfig = {
  apiKey: readEnv('VITE_FIREBASE_API_KEY'),
  authDomain: readEnv('VITE_FIREBASE_AUTH_DOMAIN'),
  projectId: readEnv('VITE_FIREBASE_PROJECT_ID'),
  storageBucket: readEnv('VITE_FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: readEnv('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  appId: readEnv('VITE_FIREBASE_APP_ID'),
  measurementId: readEnv('VITE_FIREBASE_MEASUREMENT_ID'),
};

// Debug: log mode and config
console.log('[Firebase Debug] MODE:', (import.meta as any).env?.MODE);
console.log('[Firebase Debug] Config values read:', firebaseConfig);

// Runtime sanity check for missing config (fail-closed)
(() => {
  const required: Array<keyof typeof firebaseConfig> = [
    'apiKey',
    'authDomain',
    'projectId',
    'appId',
  ];
  const missing = required.filter((k) => !firebaseConfig[k]);
  if (missing.length) {
    console.error('[Firebase] CRITICAL: Missing required config keys:', missing);
    console.error('[Firebase] Create .env.local with VITE_FIREBASE_* variables (see .env.example)');
    console.error('[Firebase] Required variables: VITE_FIREBASE_API_KEY, VITE_FIREBASE_AUTH_DOMAIN, VITE_FIREBASE_PROJECT_ID, VITE_FIREBASE_APP_ID');
  }
})();

// BUILD-TIME AUTHORITY GUARD: Prevent TrustGate builds from using wrong project
(() => {
  const mode = (import.meta as any)?.env?.MODE;
  const projectId = firebaseConfig.projectId;
  
  if (mode === 'trustgate' && projectId !== 'trustgate-27ddc') {
    const errorMsg = `[FATAL] BUILD BLOCKED: TrustGate build using wrong Firebase project.\n` +
      `Expected: trustgate-27ddc\n` +
      `Got: ${projectId}\n` +
      `Check .env.trustgate has correct VITE_FIREBASE_PROJECT_ID`;
    console.error(errorMsg);
    if (typeof window !== 'undefined') {
      throw new Error(errorMsg);
    }
  }
  
  // Also warn if TrustGate domain is using Sotto project (runtime check)
  if (typeof window !== 'undefined') {
    const isTrustGateDomain = 
      window.location.hostname === 'trustgate.dev' ||
      window.location.hostname === 'www.trustgate.dev' ||
      window.location.hostname === 'trustgate-27ddc.web.app';
    
    if (isTrustGateDomain && projectId !== 'trustgate-27ddc') {
      console.error('[FATAL] TrustGate domain is using wrong Firebase project. Expected trustgate-27ddc, got:', projectId);
    }
  }
})();

// Initialize Firebase app ONCE (works in browser and SSR/node)
const firebaseApp: FirebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig);

// Initialize services from the single app instance
let auth: Auth;
let db: Firestore;
let storage: FirebaseStorage;
let functions: Functions;

try {
  auth = getAuth(firebaseApp);
} catch (error) {
  console.error('[Firebase] Auth initialization failed:', error);
  auth = null as any;
}

try {
  db = getFirestore(firebaseApp);
} catch (error) {
  console.error('[Firebase] Firestore initialization failed:', error);
  db = null as any;
}

try {
  storage = getStorage(firebaseApp);
} catch (error) {
  console.error('[Firebase] Storage initialization failed:', error);
  storage = null as any;
}

try {
  functions = getFunctions(firebaseApp, 'us-central1');
} catch (error) {
  console.error('[Firebase] Functions initialization failed:', error);
  functions = null as any;
}

// Runtime authority logging (once per session)
try {
  const g: any = typeof globalThis !== 'undefined' ? globalThis : {};
  if (!g.__TRUSTGATE_FB_LOGGED__ && typeof window !== 'undefined') {
    const opts = (firebaseApp as any)?.options || {};
    console.log('%c[TrustGate] Firebase Authority', 'color:#10b981;font-weight:700', {
      projectId: opts.projectId,
      authDomain: opts.authDomain,
    });
    g.__TRUSTGATE_FB_LOGGED__ = true;
  }
} catch {}

export { firebaseApp, auth, db, storage, functions };
